import { CLAIM_STATES } from '@wodi/domain';
import { z } from 'zod';

import { reasonSchema } from './clinical-common.ts';

// SHA claims and pre-authorisations (docs/plans/phase1-sha.md §8.3, §9.4, §9.5). Money is
// cents as decimal strings. States are ours; SHA's statuses and reasons are shown in SHA's
// words as returned.

export { CLAIM_STATES };
const centsString = z.string().regex(/^-?\d+$/);

export const CLAIM_VIEWS = ['needs_attention', 'in_progress', 'settled', 'all'] as const;
export type ClaimListView = (typeof CLAIM_VIEWS)[number];

export const claimListQuerySchema = z.object({
  view: z.enum(CLAIM_VIEWS).default('needs_attention'),
});

const reasonViewSchema = z.object({
  display: z.string(),
  code: z.string(),
  element: z.string().nullable(),
});

export const claimProblemSchema = z.object({
  code: z.string(),
  /** In words, e.g. "The visit has no diagnosis". */
  words: z.string(),
  /** Where it is fixed (CLAIM_READINESS_FIX in @wodi/domain). */
  fix: z.string(),
  subjectId: z.string().nullable(),
});

export const claimSummarySchema = z.object({
  id: z.uuid(),
  claimNumber: z.string(),
  patient: z.object({ id: z.uuid(), displayName: z.string(), patientNumber: z.string() }),
  visit: z.object({ id: z.uuid(), visitNumber: z.string(), startedAt: z.iso.datetime() }),
  schemeName: z.string(),
  state: z.enum(CLAIM_STATES),
  /** The state as a sentence, e.g. "Rejected: …" with SHA's reason (plan §9.4). */
  sentence: z.string(),
  claimedCents: centsString.nullable(),
  approvedCents: centsString.nullable(),
  assignedTo: z.object({ id: z.uuid(), displayName: z.string() }).nullable(),
  workState: z.enum(['none', 'needs_fix', 'fixing']),
  lastChangedAt: z.iso.datetime(),
  reasons: z.array(reasonViewSchema),
  problems: z.array(claimProblemSchema),
});
export type ClaimSummary = z.infer<typeof claimSummarySchema>;

export const claimListSchema = z.object({ items: z.array(claimSummarySchema) });

export const claimDetailSchema = claimSummarySchema.extend({
  version: z.number().int(),
  externalReference: z.string().nullable(),
  paymentReference: z.string().nullable(),
  paidCents: centsString.nullable(),
  canSubmit: z.boolean(),
  canResubmit: z.boolean(),
  lines: z.array(
    z.object({
      sequence: z.number().int(),
      code: z.string(),
      display: z.string(),
      quantity: z.number().int(),
      payerCents: centsString,
      patientCents: centsString,
      serviceDate: z.string(),
      /**
       * The line's pre-authorisation: SHA's approval, or that a senior clinician went ahead
       * without one (plan C1, provisional); null when none was linked.
       */
      preauth: z
        .object({ state: z.enum(['approved', 'overridden']), words: z.string() })
        .nullable(),
    }),
  ),
  diagnoses: z.array(
    z.object({ sequence: z.number().int(), code: z.string(), display: z.string() }),
  ),
  submissions: z.array(
    z.object({
      attempt: z.number().int(),
      state: z.enum(['queued', 'sent', 'send_failed', 'superseded']),
      sentAt: z.iso.datetime().nullable(),
      externalReference: z.string().nullable(),
    }),
  ),
  events: z.array(
    z.object({
      kind: z.string(),
      /** What happened, in words, with SHA's status words where there are any. */
      words: z.string(),
      occurredAt: z.iso.datetime(),
      actor: z.string(),
      reasons: z.array(reasonViewSchema),
    }),
  ),
  writeOffs: z.array(
    z.object({
      id: z.uuid(),
      amountCents: centsString,
      reasonCode: z.string(),
      reasonNote: z.string().nullable(),
      status: z.enum(['requested', 'approved', 'rejected', 'withdrawn']),
      requestedBy: z.string(),
      decidedBy: z.string().nullable(),
    }),
  ),
});
export type ClaimDetail = z.infer<typeof claimDetailSchema>;

export const claimSubmitRequestSchema = z.object({ expectedVersion: z.number().int().min(1) });

export const claimAssignRequestSchema = z.object({
  userId: z.union([z.uuid(), z.literal('me'), z.null()]),
});

export const WRITE_OFF_REASONS = [
  'sha_final_rejection',
  'past_deadline',
  'not_claimable',
  'other',
] as const;

export const claimWriteOffRequestSchema = z
  .object({
    amountCents: centsString,
    reasonCode: z.enum(WRITE_OFF_REASONS),
    reasonNote: z.string().trim().max(500).optional(),
  })
  .refine((v) => v.reasonCode !== 'other' || (v.reasonNote ?? '').length >= 5, {
    message: 'Say why, in a few words',
    path: ['reasonNote'],
  });

export const writeOffDecisionSchema = z.object({ note: reasonSchema.optional() });

export const prepareClaimResponseSchema = z.object({ claimId: z.uuid() });

// --- Pre-authorisation ---------------------------------------------------------------------------

/** What a pre-authorisation is for: the order or prescription line (plan §4.7). */
export const PREAUTH_SOURCE_TYPES = [
  'lab_order',
  'imaging_order',
  'procedure',
  'prescription_line',
] as const;

export const preauthRequestSchema = z.object({
  clientRequestId: z.uuid(),
  source: z.object({ type: z.enum(PREAUTH_SOURCE_TYPES), id: z.uuid() }),
  diagnosisIds: z.array(z.uuid()).min(1),
  /** Required (plan C2, provisional: TODO(clinical-verify)). PHI: never logged. */
  justification: z.string().trim().min(5).max(2000),
  quantity: z.number().int().min(1).max(1000),
});
export type PreauthRequestBody = z.infer<typeof preauthRequestSchema>;

export const preauthViewSchema = z.object({
  id: z.uuid(),
  preauthNumber: z.string(),
  visitId: z.uuid(),
  patientId: z.uuid(),
  interventionCode: z.string(),
  interventionDisplay: z.string(),
  quantity: z.number().int(),
  state: z.enum([
    'queued',
    'submitted',
    'approved',
    'declined',
    'expired',
    'cancelled',
    'send_failed',
  ]),
  /** In words: "Waiting for SHA approval", "SHA approved until 25 Oct 2026", "SHA declined: …". */
  sentence: z.string(),
  externalReference: z.string().nullable(),
  requestedAt: z.iso.datetime(),
  validUntil: z.iso.datetime().nullable(),
  events: z.array(z.object({ kind: z.string(), words: z.string(), occurredAt: z.iso.datetime() })),
});
export type PreauthView = z.infer<typeof preauthViewSchema>;

export const preauthListSchema = z.object({ items: z.array(preauthViewSchema) });

/**
 * Where a service stands with SHA approval (plan §6.4), plus `overridden`: a senior
 * clinician went ahead without an approval that couldn't be obtained (plan C1, provisional:
 * TODO(clinical-verify)).
 */
export const PREAUTH_ROW_STATES = [
  'not_required',
  'requirement_unknown',
  'needs_request',
  'request_failed',
  'waiting',
  'approved',
  'declined',
  'overridden',
] as const;
export type PreauthRowState = (typeof PREAUTH_ROW_STATES)[number];

export const preauthGateSchema = z.object({
  state: z.enum(PREAUTH_ROW_STATES),
  words: z.string(),
});

/** One ordered service on a visit and its SHA approval, for the order screens (plan §9.3). */
export const preauthGateItemSchema = preauthGateSchema.extend({
  source: z.object({ type: z.enum(PREAUTH_SOURCE_TYPES), id: z.uuid() }),
  /** The newest request that wasn't cancelled, with its history; null when none was made. */
  request: preauthViewSchema.nullable(),
  /** When and by whom the service went ahead without approval; null when it didn't. */
  override: z.object({ at: z.iso.datetime(), by: z.string() }).nullable(),
});
export type PreauthGateItem = z.infer<typeof preauthGateItemSchema>;

export const visitPreauthGatesSchema = z.object({
  /** False when the visit isn't billed to SHA: nothing needs approval. */
  sha: z.boolean(),
  /** False when no pre-authorisation list is active: "not loaded", never "not required". */
  listLoaded: z.boolean(),
  items: z.array(preauthGateItemSchema),
});
export type VisitPreauthGates = z.infer<typeof visitPreauthGatesSchema>;

/** "Go ahead without SHA approval" (plan C1, provisional: TODO(clinical-verify)). */
export const preauthOverrideSchema = z.object({ reason: reasonSchema });

export const preauthCancelSchema = z.object({ reason: reasonSchema });

/** The SHA part of a visit's bill (plan §9.7; checklist 22): amounts by intervention. */
export const visitShaAmountsSchema = z.object({
  interventions: z.array(
    z.object({
      code: z.string(),
      display: z.string(),
      quantity: z.number().int(),
      totalCents: centsString,
      shaCents: centsString,
      patientCents: centsString,
    }),
  ),
  totals: z.object({ totalCents: centsString, shaCents: centsString, patientCents: centsString }),
  /** Charges not on the claim, with why (INTERVENTION_EXCLUSIONS). */
  excluded: z.array(z.object({ chargeId: z.uuid(), reason: z.string(), words: z.string() })),
  claim: z.object({ id: z.uuid(), claimNumber: z.string(), sentence: z.string() }).nullable(),
});
export type VisitShaAmounts = z.infer<typeof visitShaAmountsSchema>;
